import { useCallback } from "react";
import { useToken } from "../../../server/auth/use-token";
import { incrementFileName } from "../../../utils/increment-file-name";
import { NameSpaceURL, uuidv4, uuidv5 } from "../../../utils/uuid";
import { useDesignHuddleProjectClone } from "../../design-huddle/use-design-huddle-project-clone";
import { copyImage } from "../../design-huddle/utils";
import { useCreateAssetCallback } from "./use-upsert-asset";

import type { Asset } from "../server/digital-asset-service";
import type { AssetResult } from "./use-upsert-asset";

/** The created copy, or why it couldn't be saved. */
const created = (result: AssetResult): Asset => {
  if (!result.success) throw new Error(result.error?.message ?? "Failed to save the copy");
  return result.asset;
};

/** Duplicates an uploaded file: the file copied, and named "{title} (1)". Throws when it fails. */
export const useDuplicateAssetCallback = (): ((asset: Asset) => Promise<Asset>) => {
  const getToken = useToken();
  const createAsset = useCreateAssetCallback();

  return useCallback(
    async (asset: Asset) => {
      if (!asset.public_url) {
        throw new Error(`No public URL available for asset ${asset.title}.`);
      }

      const copyFileResponse = await copyImage(
        getToken,
        asset.public_url,
        uuidv5(NameSpaceURL, uuidv4()),
      );

      if (!copyFileResponse.success) throw new Error(`Failed to copy the file of ${asset.title}.`);

      return created(
        await createAsset({
          title: incrementFileName(asset.title),
          thumbnailUrl: asset.thumbnail_url ? copyFileResponse.url : undefined,
          publicUrl: copyFileResponse.url,
          contentType: asset.content_type,
          projectId: asset.project_id,
          pageId: asset.page_id,
          mimeType: asset.mime_type,
          sourceDigitalAssetId: asset.id,
          pageCount: asset.page_count,
          folderId: asset.folder_id,
        }),
      );
    },
    [createAsset, getToken],
  );
};

/** Duplicates a design: its Design Huddle project cloned, and named "Copy of {title}". Throws when it fails. */
export const useDuplicateDesignAssetCallback = (): ((asset: Asset) => Promise<Asset>) => {
  const cloneProject = useDesignHuddleProjectClone();
  const createAsset = useCreateAssetCallback();

  return useCallback(
    async (asset: Asset) => {
      if (!asset.project_id) throw new Error(`No project to copy for asset ${asset.title}.`);

      const projectId = await cloneProject(asset.project_id, asset.page_id);
      return created(
        await createAsset({
          projectId,
          contentType: asset.content_type,
          thumbnailUrl: asset.thumbnail_url,
          publicUrl: asset.public_url,
          pageId: asset.page_id,
          title: `Copy of ${asset.title}`,
          pageCount: asset.page_count,
          folderId: asset.folder_id,
        }),
      );
    },
    [cloneProject, createAsset],
  );
};

/** Duplicates either kind, as the Content Library's preview does for each. Throws when it fails. */
export const useDuplicateAnyAssetCallback = (): ((asset: Asset) => Promise<Asset>) => {
  const duplicateFile = useDuplicateAssetCallback();
  const duplicateDesign = useDuplicateDesignAssetCallback();

  return useCallback(
    (asset: Asset) =>
      asset.content_type === "Uploaded File" ? duplicateFile(asset) : duplicateDesign(asset),
    [duplicateDesign, duplicateFile],
  );
};
